import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, CircularProgress, useMediaQuery, MenuItem, Select } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Text from '../components/Text';
import Input from '../components/Input';
import GlassButton from '../components/GlassButton';
import NodeAura from '../components/dynamic/NodeAura';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';

const NODE_LOGO_URL = './apple-touch-icon.png';

const Login = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetPasswordFlow, setIsResetPasswordFlow] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const [step, setStep] = useState(0);
  const shouldRestoreIntendedUrl = useRef(
    sessionStorage.getItem('freshProtectedLoginRedirect') === 'true'
  );

  useEffect(() => {
    localStorage.removeItem('lastLoginEmail');
    sessionStorage.removeItem('freshProtectedLoginRedirect');
    if (shouldRestoreIntendedUrl.current) {
      sessionStorage.removeItem('showPostLoginChecklist');
    } else {
      sessionStorage.removeItem('intendedUrl');
    }
  }, []);

  const recentBusinesses = useMemo(() => {
    try {
      const raw = localStorage.getItem('recentBusinesses');
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return [];
  }, []);

  const [selectedBusinessId, setSelectedBusinessId] = useState(() => {
    if (recentBusinesses.length > 0) return recentBusinesses[0].id;
    return '';
  });

  const selectedBranding = useMemo(() => {
    if (!selectedBusinessId) return null;
    return recentBusinesses.find((b) => b.id === selectedBusinessId) || null;
  }, [selectedBusinessId, recentBusinesses]);

  useEffect(() => {
    if (selectedBusinessId) {
      localStorage.setItem('businessId', selectedBusinessId);
      window.dispatchEvent(
        new CustomEvent('node:auth-branding-change', {
          detail: selectedBranding,
        })
      );
    }
  }, [selectedBranding, selectedBusinessId]);

  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 80);
    const t2 = setTimeout(() => setStep(2), 250);
    const t3 = setTimeout(() => setStep(3), 450);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const reveal = (idx, delay = 0) => ({
    opacity: step >= idx ? 1 : 0,
    transform: step >= idx ? 'translateY(0)' : 'translateY(14px)',
    transition: `all 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
  });

  const mutedText = isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.42)';
  const accentColor = selectedBranding?.mainColor || theme.palette.primary.main;

  const isTransientAuthError = (code) =>
    code === 'auth/network-request-failed' || code === 'auth/internal-error';

  const getLoginErrorMessage = (err) => {
    if (err?.code === 'auth/invalid-email') return t('auth/invalid-email');
    if (err?.code === 'auth/user-not-found') return t('auth/user-not-found');
    if (err?.code === 'auth/wrong-password') return t('auth/wrong-password');
    if (err?.code === 'auth/too-many-requests')
      return t('auth/too-many-requests');
    return t('errorLoggingIn');
  };

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setIsLoading(true);

    try {
      const response = await axios({
        method: 'POST',
        url: 'https://api.usenode.com/users/check-email',
        headers: { 'Content-Type': 'application/json' },
        data: { email: email.trim() },
        timeout: 10000,
      });
      if (response?.data?.exists) {
        setEmailValidated(true);
      } else {
        setError(t('noAccountFound') || 'Aucun compte trouvé pour ce courriel.');
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        setError(t('tooManyAttempts') || 'Trop de tentatives. Réessayez dans une minute.');
      } else {
        setError(t('errorCheckingEmail') || 'Erreur lors de la vérification.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);
    const shouldShowChecklist = !shouldRestoreIntendedUrl.current;
    let loginSucceeded = false;

    if (shouldShowChecklist) {
      sessionStorage.setItem('showPostLoginChecklist', 'true');
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
      loginSucceeded = true;
    } catch (err) {
      if (isTransientAuthError(err?.code)) {
        try {
          await new Promise((r) => setTimeout(r, 400));
          await signInWithEmailAndPassword(auth, email, password);
          loginSucceeded = true;
        } catch (retryErr) {
          setError(getLoginErrorMessage(retryErr));
        }
      } else {
        setError(getLoginErrorMessage(err));
      }
    } finally {
      if (!loginSucceeded && shouldShowChecklist) {
        sessionStorage.removeItem('showPostLoginChecklist');
      }
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError(t('pleaseEnterEmail'));
      return;
    }

    setError(null);
    setSuccessMessage(null);

    setIsLoading(true);

    try {
      await nodeAxiosFirebase({
        method: 'POST',
        url: 'users/password',
        errorToast: t('errorForgotPassword'),
        body: { email },
      });
      setSuccessMessage(t('passwordResetEmailSent'));
    } catch (err) {
      setError(err.message || t('errorForgotPassword'));
    } finally {
      setIsLoading(false);
    }
  };

  const showResetPasswordFlow = () => {
    setError(null);
    setSuccessMessage(null);
    setPassword('');
    setIsResetPasswordFlow(true);
  };

  const showLoginFlow = () => {
    setError(null);
    setSuccessMessage(null);
    setIsResetPasswordFlow(false);
  };

  const secondaryText = isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.52)';
  const borderColor = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  return (
    <Box>

      {/* Logo collaboration animation */}
      <Box sx={{ ...reveal(1), mb: 3 }}>
        {selectedBranding ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              '@keyframes bumpLeft': {
                '0%, 100%': { transform: 'translateX(0)' },
                '50%': { transform: 'translateX(4px)' },
              },
              '@keyframes bumpRight': {
                '0%, 100%': { transform: 'translateX(0)' },
                '50%': { transform: 'translateX(-4px)' },
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: isDarkMode ? 'rgba(255,255,255,0.08)' : '#fff',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                animation: 'bumpLeft 2.5s ease-in-out infinite',
              }}
            >
              <Box
                component="img"
                src={NODE_LOGO_URL}
                alt="Node"
                sx={{ height: 20, width: 'auto', objectFit: 'contain' }}
              />
            </Box>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: isDarkMode ? 'rgba(255,255,255,0.08)' : '#fff',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '-8px',
                zIndex: 1,
                animation: 'bumpRight 2.5s ease-in-out infinite',
              }}
            >
              <Box
                component="img"
                src={`https://storage.googleapis.com/business-logos-node/${selectedBranding.id}.png`}
                alt={selectedBranding.name}
                sx={{
                  height: 28,
                  width: 28,
                  objectFit: 'cover',
                  borderRadius: '50%',
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            component="img"
            src={NODE_LOGO_URL}
            alt="Node"
            sx={{
              height: isMobile ? 14 : 18,
              width: 'auto',
              objectFit: 'contain',
            }}
          />
        )}
      </Box>

      {/* Header */}
      <Box sx={{ ...reveal(1, 60), mb: 0.5 }}>
        <Text
          value={t('welcome')}
          fontSize={isMobile ? 28 : 32}
          fontWeight={700}
          sx={{ letterSpacing: '-0.03em', lineHeight: 1}}
        />
      </Box>
      <Box sx={{ ...reveal(1, 80), mb: isMobile ? 3.5 : 4.5 }}>
        <Text
          value={
            isResetPasswordFlow
              ? 'Entrez votre courriel pour recevoir un lien de réinitialisation.'
              : selectedBranding?.name
                ? `Connectez-vous pour accéder à ${selectedBranding.name}.`
                : 'Connectez-vous pour accéder à votre espace.'
          }
          fontSize={14}
          fontWeight={400}
          sx={{ color: secondaryText, lineHeight: 1.55 }}
        />
      </Box>

      {/* Business selector */}
      {recentBusinesses.length > 0 && (
        <Box sx={{ ...reveal(1, 100), mb: 2 }}>
          <Select
            value={selectedBusinessId}
            onChange={(e) => setSelectedBusinessId(e.target.value)}
            size="small"
            fullWidth
            sx={{
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: 500,
              background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
              },
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                py: '8px',
              },
            }}
            renderValue={(value) => {
              const biz = recentBusinesses.find((b) => b.id === value);
              if (!biz) return '';
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Box
                    component="img"
                    src={`https://storage.googleapis.com/business-logos-node/${biz.id}.png`}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '6px',
                      objectFit: 'cover',
                    }}
                  />
                  <span>{biz.name}</span>
                </Box>
              );
            }}
          >
            {recentBusinesses.map((biz) => (
              <MenuItem key={biz.id} value={biz.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Box
                    component="img"
                    src={`https://storage.googleapis.com/business-logos-node/${biz.id}.png`}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '6px',
                      objectFit: 'cover',
                    }}
                  />
                  <span style={{ fontSize: '13px', fontWeight: 500 }}>{biz.name}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {/* Form */}
      <Box
        component="form"
        onSubmit={
          isResetPasswordFlow
            ? handleForgotPassword
            : emailValidated
              ? handleLogin
              : handleCheckEmail
        }
      >
        <Box sx={{ ...reveal(2), mb: 2.5 }}>
          <Input
            name="email"
            label={t('email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            autoComplete="username"
            autoFocus
            required
            fullWidth
            disabled={isLoading}
            InputProps={{ readOnly: emailValidated }}
          />
          {emailValidated && !isResetPasswordFlow && (
            <Box
              component="span"
              onClick={() => {
                setEmailValidated(false);
                setPassword('');
                setError(null);
              }}
              sx={{
                fontSize: '11px',
                fontWeight: 500,
                color: accentColor,
                cursor: 'pointer',
                mt: 0.5,
                display: 'inline-block',
              }}
            >
              {t('changeEmail') || 'Modifier'}
            </Box>
          )}
        </Box>

        {!isResetPasswordFlow && emailValidated && (
          <Box sx={{ ...reveal(2, 80), mb: 1.5 }}>
            <Input
              name="password"
              label={t('password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              autoComplete="current-password"
              autoFocus
              required
              fullWidth
              disabled={isLoading}
            />
          </Box>
        )}

        {/* Forgot password */}
        {!isResetPasswordFlow && emailValidated && (
          <Box
            sx={{
              ...reveal(2, 120),
              display: 'flex',
              justifyContent: 'flex-end',
              mb: 4.5,
            }}
          >
            <Box
              component="span"
              onClick={!isLoading ? showResetPasswordFlow : undefined}
              sx={{
                fontSize: '11.5px',
                fontWeight: 500,
                color: isDarkMode
                  ? 'rgba(255,255,255,0.4)'
                  : 'rgba(0,0,0,0.38)',
                cursor: !isLoading ? 'pointer' : 'default',
                opacity: !isLoading ? 1 : 0.5,
                transition: 'all 0.2s ease',
                fontFamily:
                  "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                '&:hover':
                  !isLoading
                    ? {
                      color: isDarkMode
                        ? 'rgba(255,255,255,0.6)'
                        : 'rgba(0,0,0,0.55)',
                    }
                    : {},
              }}
            >
              {t('forgotPassword')}
            </Box>
          </Box>
        )}

        {isResetPasswordFlow && (
          <Box
            sx={{
              ...reveal(2, 120),
              display: 'flex',
              justifyContent: 'flex-end',
              mb: 4.5,
            }}
          >
            <Box
              component="span"
              onClick={!isLoading ? showLoginFlow : undefined}
              sx={{
                fontSize: '11.5px',
                fontWeight: 500,
                color: isDarkMode
                  ? 'rgba(255,255,255,0.4)'
                  : 'rgba(0,0,0,0.38)',
                cursor: !isLoading ? 'pointer' : 'default',
                opacity: !isLoading ? 1 : 0.5,
                transition: 'all 0.2s ease',
                fontFamily:
                  "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                '&:hover':
                  !isLoading
                    ? {
                      color: isDarkMode
                        ? 'rgba(255,255,255,0.6)'
                        : 'rgba(0,0,0,0.55)',
                    }
                    : {},
              }}
            >
              Retour à la connexion
            </Box>
          </Box>
        )}

        {/* Submit button */}
        <Box sx={{ ...reveal(3), textAlign: 'center'  }}>
          <GlassButton
            type="submit"
            variant="contained"
            disabled={
              isResetPasswordFlow
                ? !email || isLoading
                : emailValidated
                  ? !password || isLoading
                  : !email || isLoading
            }
            fullWidth
            sx={{
              maxWidth: '400px',
              width: '100%',
              py: 1.4,
              ...(selectedBranding && {
                background: accentColor,
                '&:hover': {
                  background: `${accentColor}dd`,
                },
              }),
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <CircularProgress size={16} color="inherit" />
                <span>
                  {isResetPasswordFlow
                    ? 'Envoi du lien...'
                    : emailValidated
                      ? t('loggingIn')
                      : t('verifying') || 'Vérification...'}
                </span>
              </Box>
            ) : (
              isResetPasswordFlow
                ? 'Envoyer le lien de réinitialisation'
                : emailValidated
                  ? t('login')
                  : t('continue') || 'Continuer'
            )}
          </GlassButton>
        </Box>
      </Box>

      {/* Error / Success messages */}
      {error && (
        <Box
          sx={{
            mt: 3,
            px: 2,
            py: 1.2,
            borderRadius: '10px',
            background: isDarkMode
              ? 'rgba(239,68,68,0.08)'
              : 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.12)',
          }}
        >
          <Text
            value={error}
            fontSize={12}
            fontWeight={500}
            sx={{ color: '#ef4444', textAlign: 'center' }}
          />
        </Box>
      )}

      {successMessage && (
        <Box
          sx={{
            mt: 3,
            px: 2,
            py: 1.2,
            borderRadius: '10px',
            background: isDarkMode
              ? 'rgba(16,185,129,0.08)'
              : 'rgba(16,185,129,0.06)',
            border: '1px solid rgba(16,185,129,0.12)',
          }}
        >
          <Text
            value={successMessage}
            fontSize={12}
            fontWeight={500}
            sx={{ color: '#10b981', textAlign: 'center' }}
          />
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ ...reveal(3, 150), mt: 7 }}>
        <Box
          sx={{
            height: '1px',
            background: borderColor,
            mb: 3,
          }}
        />
        <Box
          sx={{
            textAlign: 'center',
            fontSize: '11px',
            fontWeight: 400,
            color: mutedText,
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            lineHeight: 1.5,
            opacity: 0.7,
          }}
        >
          Besoin d'aide{'\u00a0'}? Contactez votre administrateur.
        </Box>
        <Box
          sx={{
            textAlign: 'center',
            fontSize: '10px',
            fontWeight: 400,
            color: mutedText,
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            mt: 1,
            opacity: 0.5,
          }}
        >
          v{process.env.REACT_APP_VERSION || '—'}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
